import { roundCurrencyAmount } from '@/lib/format'
import type { LineItem, Surcharge, TradeDiscountType } from '@/types'

export interface TradeDiscountInput {
  type?: TradeDiscountType | null
  value?: number | null
}

export interface DocumentTaxBreakdownRow {
  rate: number
  base: number
  tax: number
}

export interface DocumentTotalsResult {
  subtotal: number
  surcharge_total: number
  discount_type: TradeDiscountType | null
  discount_value: number
  discount_amount: number
  tax_total: number
  tax_breakdown: DocumentTaxBreakdownRow[]
  total: number
}

export function computeDocumentTotals(
  items: LineItem[],
  surcharges: Surcharge[] = [],
  discount?: TradeDiscountInput | null
): DocumentTotalsResult {
  let subtotal = 0
  let surchargeTotal = 0
  const taxableBaseByRate = new Map<string, number>()

  items.forEach(item => {
    const quantity = item.quantity || 0
    const unitPrice = item.unit_price || 0
    const taxRate = item.tax_rate || 0
    const lineBase = roundCurrencyAmount(quantity * unitPrice)

    subtotal += lineBase
    accumulateRateBase(taxableBaseByRate, taxRate, lineBase)
  })

  surcharges.forEach(surcharge => {
    const amount = roundCurrencyAmount(surcharge.amount || 0)
    const taxRate = readSurchargeTaxRate(surcharge)

    surchargeTotal += amount
    accumulateRateBase(taxableBaseByRate, taxRate, amount)
  })

  subtotal = roundCurrencyAmount(subtotal)
  surchargeTotal = roundCurrencyAmount(surchargeTotal)
  const baseTotal = roundCurrencyAmount(subtotal + surchargeTotal)
  const [discountType, discountValue, discountAmount] = resolveDiscount(
    discount,
    baseTotal
  )
  const discountedBases = allocateDiscount(taxableBaseByRate, discountAmount)

  let taxTotal = 0
  const taxBreakdown = Array.from(discountedBases.entries())
    .sort(([a], [b]) => Number(a) - Number(b))
    .reduce<DocumentTaxBreakdownRow[]>((rows, [rateKey, base]) => {
      if (base <= 0) return rows

      const rate = Number(rateKey)
      const roundedBase = roundCurrencyAmount(base)
      const tax = roundCurrencyAmount(roundedBase * (rate / 100))

      taxTotal += tax
      rows.push({
        rate,
        base: roundedBase,
        tax,
      })
      return rows
    }, [])

  taxTotal = roundCurrencyAmount(taxTotal)

  return {
    subtotal,
    surcharge_total: surchargeTotal,
    discount_type: discountType,
    discount_value: discountValue,
    discount_amount: discountAmount,
    tax_total: taxTotal,
    tax_breakdown: taxBreakdown,
    total: roundCurrencyAmount(baseTotal - discountAmount + taxTotal),
  }
}

function accumulateRateBase(
  taxableBaseByRate: Map<string, number>,
  rate: number,
  amount: number
) {
  const key = rate.toFixed(2)
  taxableBaseByRate.set(
    key,
    roundCurrencyAmount((taxableBaseByRate.get(key) ?? 0) + amount)
  )
}

function resolveDiscount(
  discount: TradeDiscountInput | null | undefined,
  baseTotal: number
): [TradeDiscountType | null, number, number] {
  const type = discount?.type
  const value = roundCurrencyAmount(Number(discount?.value ?? 0))

  if (
    (type !== 'percent' && type !== 'fixed') ||
    value <= 0 ||
    baseTotal <= 0
  ) {
    return [null, 0, 0]
  }

  const amount =
    type === 'percent'
      ? roundCurrencyAmount((baseTotal * Math.min(value, 100)) / 100)
      : Math.min(value, baseTotal)

  return [type, value, roundCurrencyAmount(amount)]
}

function allocateDiscount(
  taxableBaseByRate: Map<string, number>,
  discountAmount: number
) {
  const discountedBases = new Map(
    Array.from(taxableBaseByRate.entries()).map(([key, value]) => [
      key,
      roundCurrencyAmount(value),
    ])
  )
  const entries = Array.from(discountedBases.entries())
  const baseTotal = roundCurrencyAmount(
    entries.reduce((sum, [, value]) => sum + value, 0)
  )

  if (discountAmount <= 0 || baseTotal <= 0) {
    return discountedBases
  }

  let remainingDiscount = discountAmount
  entries.forEach(([key, base], index) => {
    const allocated =
      index === entries.length - 1
        ? remainingDiscount
        : roundCurrencyAmount((discountAmount * base) / baseTotal)
    const applied = Math.min(allocated, base, remainingDiscount)

    discountedBases.set(key, roundCurrencyAmount(Math.max(0, base - applied)))
    remainingDiscount = roundCurrencyAmount(remainingDiscount - applied)
  })

  return discountedBases
}

function readSurchargeTaxRate(surcharge: Surcharge): number {
  return surcharge.tax_rate ?? 0
}
